import { sendRequest } from '@infrastructure/api/api';
import { Department, DepartmentQueryParams, DepartmentListResponse } from '../types/department';
import ApiResponse from '@core/types/apiResponse';
import { UpdateDepartmentRequest } from '../types/departmentRequest';
import { buildDepartmentQueryPath, buildDepartmentQueryString } from './departmentQueryString';

export { buildDepartmentQueryString } from './departmentQueryString';

/**
 * Fetches a list of departments based on query parameters.
 * Uses scoped path when churchId, districtId, or nationId is set; otherwise department/query.
 * Query params use the API spec names (perPage, departmentName, departmentId, etc.).
 * When using unscoped path with a scope in params, adds scope and scope_ref_id to the query.
 * @param params - Query parameters and optional scope (churchId, districtId, nationId)
 * @returns Promise<ApiResponse<DepartmentListResponse>>
 */
export const fetchDepartments = async (params: DepartmentQueryParams = {}): Promise<ApiResponse<DepartmentListResponse>> => {
  const path = buildDepartmentQueryPath(params);
  const queryString = buildDepartmentQueryString(params);
  return await sendRequest<any, DepartmentListResponse>('GET', `/${path}?${queryString}`);
};

/**
 * Fetches a single department by ID.
 * When scope (churchId, districtId, nationId) is provided, uses the scoped endpoint.
 * @param id - Department ID
 * @param scope - Optional scope for scoped endpoint
 */
export const fetchDepartmentById = async (
  id: number,
  scope?: { churchId?: number; districtId?: number; nationId?: number }
): Promise<ApiResponse<DepartmentListResponse>> => {
  const queryParams: Record<string, string> = { departmentId: id.toString() };
  if (scope?.churchId) {
    return await sendRequest<any, DepartmentListResponse>('GET', `church/${scope.churchId}/department/query?${new URLSearchParams(queryParams)}`);
  }
  if (scope?.districtId) {
    return await sendRequest<any, DepartmentListResponse>('GET', `district/${scope.districtId}/department/query?${new URLSearchParams(queryParams)}`);
  }
  if (scope?.nationId) {
    return await sendRequest<any, DepartmentListResponse>('GET', `nation/${scope.nationId}/department/query?${new URLSearchParams(queryParams)}`);
  }
  return await sendRequest<any, DepartmentListResponse>('GET', `/department/query?${new URLSearchParams(queryParams)}`);
};

/**
 * Creates a new department
 * @param nationId - The ID of the nation
 * @param departmentData - Department creation data
 * @returns Promise<ApiResponse<Department>>
 */
export const createDepartment = async (nationId: number, departmentData: { name: string; description?: string }): Promise<ApiResponse<Department>> => {
  return await sendRequest<any, Department>('POST', `/nation/${nationId}/department/create`, departmentData);
};

/**
 * Updates an existing department
 * @param nationId - The ID of the nation
 * @param departmentId - The ID of the department to update
 * @param departmentData - Department update data
 * @returns Promise<ApiResponse<Department>>
 */
export const updateDepartment = async (nationId: number, departmentId: number, departmentData: UpdateDepartmentRequest): Promise<ApiResponse<Department>> => {
  return await sendRequest<any, Department>('POST', `/nation/${nationId}/department/${departmentId}/update`, departmentData);
};

/**
 * Disables a department (sets it inactive).
 * @param nationId - The ID of the nation
 * @param departmentId - The ID of the department to disable
 */
export const disableDepartment = async (nationId: number, departmentId: number): Promise<ApiResponse<{ message: string }>> => {
  return await sendRequest<any, { message: string }>('POST', `/nation/${nationId}/department/${departmentId}/disable`);
};

/**
 * Enables a department (sets it active).
 * @param nationId - The ID of the nation
 * @param departmentId - The ID of the department to enable
 */
export const enableDepartment = async (nationId: number, departmentId: number): Promise<ApiResponse<{ message: string }>> => {
  return await sendRequest<any, { message: string }>('POST', `/nation/${nationId}/department/${departmentId}/enable`);
};
